/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * New message, reply and forward, plus the "Draft with AI" entry point.
 *
 * Recipients are a controlled Autocomplete now. It used to be uncontrolled, so
 * a reply's pre-filled To never showed as a chip, and picking any recipient
 * replaced the reply address without the user seeing it go.
 *
 * AI drafting is the AI_EMAIL module. Without it the button is a small locked
 * chip that says why, not a dimmed icon plus a full-width Alert.
 */

import React from 'react';
import PropTypes from 'prop-types';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SendIcon from '@mui/icons-material/Send';
import { formatFileSize, parseAddress, splitAddressList } from '../emailFormat';

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike', 'blockquote'],
    [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
    ['link', 'image'],
    ['clean'],
  ],
};

const QUILL_FORMATS = ['header', 'bold', 'italic', 'underline', 'strike', 'blockquote', 'list', 'bullet', 'indent', 'link', 'image'];

const EMAIL_PATTERN = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

export const splitAddresses = (value) => splitAddressList(value);

// Accepts `jane@x.com` and `Jane Doe <jane@x.com>`; send strips the name.
export const isValidEmail = (value) => EMAIL_PATTERN.test(String(parseAddress(value).email || '').trim());

const quillSx = (theme) => ({
  '& .ql-toolbar.ql-snow': {
    borderColor: theme.palette.divider,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    bgcolor: theme.palette.background.default,
  },
  '& .ql-container.ql-snow': {
    borderColor: theme.palette.divider,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    fontFamily: theme.typography.fontFamily,
    fontSize: '0.9375rem',
  },
  '& .ql-editor': { minHeight: 240, color: theme.palette.text.primary },
  '& .ql-editor.ql-blank::before': { color: theme.palette.text.secondary, fontStyle: 'normal' },
  '& .ql-snow .ql-stroke': { stroke: theme.palette.text.secondary },
  '& .ql-snow .ql-fill': { fill: theme.palette.text.secondary },
  '& .ql-snow .ql-picker': { color: theme.palette.text.secondary },
  '& .ql-snow .ql-picker-options': { bgcolor: theme.palette.background.paper },
  '& .ql-snow button:hover .ql-stroke, & .ql-snow button.ql-active .ql-stroke': { stroke: theme.palette.primary.main },
  '& .ql-snow button:hover .ql-fill, & .ql-snow button.ql-active .ql-fill': { fill: theme.palette.primary.main },
  '& .ql-snow .ql-picker-label:hover, & .ql-snow .ql-picker-label.ql-active, & .ql-snow .ql-picker-item:hover, & .ql-snow .ql-picker-item.ql-selected': {
    color: theme.palette.primary.main,
  },
});

function AiDraftButton({ enabled, licenseLoading, generating, onClick }) {
  if (licenseLoading) return null;
  if (!enabled) {
    return (
      <Tooltip title="AI drafting is not included in your plan. Ask your administrator to turn it on.">
        <Chip
          size="small"
          variant="outlined"
          icon={<LockOutlinedIcon sx={{ fontSize: 14 }} />}
          label="Draft with AI"
          sx={{ color: 'text.secondary', borderStyle: 'dashed' }}
        />
      </Tooltip>
    );
  }
  return (
    <Button
      size="small"
      variant="outlined"
      onClick={onClick}
      disabled={generating}
      startIcon={generating ? <CircularProgress size={14} /> : <AutoAwesomeOutlinedIcon fontSize="small" />}
    >
      {generating ? 'Drafting...' : 'Draft with AI'}
    </Button>
  );
}

AiDraftButton.propTypes = {
  enabled: PropTypes.bool,
  licenseLoading: PropTypes.bool,
  generating: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
};

AiDraftButton.defaultProps = { enabled: false, licenseLoading: false, generating: false };

function ComposePane({
  title,
  draft,
  onDraftChange,
  showCc,
  showBcc,
  onToggleCc,
  onToggleBcc,
  recipientOptions,
  recipientsLoading,
  onRecipientSearch,
  attachments,
  uploadingAttachment,
  onAttach,
  onRemoveAttachment,
  draftId,
  savingDraft,
  sending,
  onSaveDraft,
  onDiscard,
  onSend,
  aiEnabled,
  licenseLoading,
  generating,
  aiError,
  aiDialogOpen,
  aiContext,
  onAiContextChange,
  onOpenAiDialog,
  onCloseAiDialog,
  onGenerate,
}) {
  const toList = splitAddresses(draft.to);
  const invalidTo = toList.filter((address) => !isValidEmail(address));
  const canSend = toList.length > 0 && invalidTo.length === 0 && !sending;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.25, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle1" component="h2" sx={{ flex: 1 }} noWrap>
          {title}
        </Typography>
        <AiDraftButton enabled={aiEnabled} licenseLoading={licenseLoading} generating={generating} onClick={onOpenAiDialog} />
        <Tooltip title="Discard">
          <IconButton size="small" onClick={onDiscard} aria-label="Discard">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', px: { xs: 2, md: 3 }, py: 2, minHeight: 0 }}>
        <Autocomplete
          multiple
          freeSolo
          autoSelect
          filterSelectedOptions
          options={recipientOptions}
          loading={recipientsLoading}
          value={toList}
          isOptionEqualToValue={(option, value) =>
            (typeof option === 'string' ? option : option.email) === (typeof value === 'string' ? value : value.email)
          }
          getOptionLabel={(option) => (typeof option === 'string' ? option : `${option.name} <${option.email}>`)}
          onChange={(event, newValue) => {
            const picked = newValue.map((value) => (typeof value === 'string' ? { email: value.trim() } : value));
            onDraftChange({
              to: picked.map((value) => value.email).filter(Boolean).join(', '),
              toIds: picked.map((value) => value.id).filter(Boolean),
            });
          }}
          onInputChange={(event, value) => onRecipientSearch(value)}
          filterOptions={(options, params) => {
            const search = params.inputValue.toLowerCase();
            const filtered = options.filter(
              (option) =>
                typeof option !== 'string' &&
                ((option.name || '').toLowerCase().includes(search) || (option.email || '').toLowerCase().includes(search))
            );
            if (search.includes('@') && !filtered.some((option) => option.email.toLowerCase() === search)) {
              filtered.push(params.inputValue);
            }
            return filtered;
          }}
          renderTags={(value, getTagProps) =>
            value.map((address, index) => {
              const label = typeof address === 'string' ? address : address.email;
              return (
                <Chip
                  {...getTagProps({ index })}
                  key={label}
                  size="small"
                  label={label}
                  color={isValidEmail(label) ? 'default' : 'error'}
                  variant={isValidEmail(label) ? 'filled' : 'outlined'}
                />
              );
            })
          }
          renderOption={(props, option) => (
            <li {...props} key={typeof option === 'string' ? option : `${option.type}-${option.id}-${option.email}`}>
              {typeof option === 'string' ? (
                <Typography variant="body2">{`Send to ${option}`}</Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="body2">{option.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.email}
                    {option.type ? ` · ${option.type}` : ''}
                  </Typography>
                </Box>
              )}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              margin="dense"
              label="To"
              placeholder={toList.length ? '' : 'Name or email address'}
              error={invalidTo.length > 0}
              helperText={invalidTo.length ? `Check ${invalidTo.length === 1 ? 'this address' : 'these addresses'}: ${invalidTo.join(', ')}` : ''}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {params.InputProps.endAdornment}
                    <InputAdornment position="end" sx={{ gap: 0.5, ml: 0.5 }}>
                      {!showCc && (
                        <Button size="small" onClick={onToggleCc} sx={{ minWidth: 0, px: 0.75 }}>
                          Cc
                        </Button>
                      )}
                      {!showBcc && (
                        <Button size="small" onClick={onToggleBcc} sx={{ minWidth: 0, px: 0.75 }}>
                          Bcc
                        </Button>
                      )}
                    </InputAdornment>
                  </>
                ),
              }}
            />
          )}
        />
        {showCc && (
          <TextField
            fullWidth
            size="small"
            margin="dense"
            label="Cc"
            placeholder="Separate addresses with commas"
            value={draft.cc}
            onChange={(e) => onDraftChange({ cc: e.target.value })}
          />
        )}
        {showBcc && (
          <TextField
            fullWidth
            size="small"
            margin="dense"
            label="Bcc"
            placeholder="Separate addresses with commas"
            value={draft.bcc}
            onChange={(e) => onDraftChange({ bcc: e.target.value })}
          />
        )}
        <TextField
          fullWidth
          size="small"
          margin="dense"
          label="Subject"
          value={draft.subject}
          onChange={(e) => onDraftChange({ subject: e.target.value })}
        />

        <Box sx={(theme) => ({ mt: 1.5, position: 'relative', ...quillSx(theme) })}>
          <ReactQuill
            value={draft.content}
            onChange={(content) => onDraftChange({ content })}
            modules={QUILL_MODULES}
            formats={QUILL_FORMATS}
          />
          {generating && (
            <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', bgcolor: 'action.hover', borderRadius: 2 }}>
              <CircularProgress size={28} />
            </Box>
          )}
        </Box>
        {aiError && (
          <Typography variant="caption" color="error" component="p" sx={{ mt: 1 }}>
            {aiError}
          </Typography>
        )}

        {attachments.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
            {attachments.map((attachment, index) => (
              <Chip
                key={`${attachment.filename}-${index}`}
                variant="outlined"
                icon={<AttachFileIcon sx={{ fontSize: 16 }} />}
                label={`${attachment.filename} (${formatFileSize(attachment.size)})`}
                onDelete={() => onRemoveAttachment(index)}
                sx={{ borderRadius: 2, maxWidth: 320 }}
              />
            ))}
          </Box>
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1.25,
          borderTop: 1,
          borderColor: 'divider',
          flexWrap: 'wrap',
        }}
      >
        <Button variant="contained" onClick={onSend} disabled={!canSend} startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}>
          {sending ? 'Sending...' : 'Send'}
        </Button>
        <Button variant="outlined" onClick={() => onSaveDraft(draftId)} disabled={savingDraft}>
          {savingDraft ? 'Saving...' : draftId ? 'Update draft' : 'Save draft'}
        </Button>
        {draftId && (
          <Button variant="text" onClick={() => onSaveDraft(null)} disabled={savingDraft}>
            Save as new draft
          </Button>
        )}
        <Tooltip title="Attach files (25 MB each)">
          <span>
            <IconButton component="label" disabled={uploadingAttachment} aria-label="Attach files">
              {uploadingAttachment ? <CircularProgress size={20} /> : <AttachFileIcon />}
              <input type="file" hidden multiple onChange={onAttach} />
            </IconButton>
          </span>
        </Tooltip>
        <Box sx={{ flex: 1 }} />
        <Tooltip
          title="To stay out of spam folders: use a clear subject, avoid ALL CAPS and phrases like 'act now', and keep attachments small."
          placement="top-end"
        >
          <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} aria-label="Deliverability tips" />
        </Tooltip>
        <Button variant="text" color="inherit" onClick={onDiscard} sx={{ color: 'text.secondary' }}>
          Discard
        </Button>
      </Box>

      <Dialog open={aiDialogOpen} onClose={onCloseAiDialog} fullWidth maxWidth="sm">
        <DialogTitle sx={{ pb: 1 }}>Draft with AI</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Describe who you&apos;re writing to and what you need to say. The draft replaces the subject and body; you can edit it before sending.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={5}
            value={aiContext}
            onChange={(e) => onAiContextChange(e.target.value)}
            placeholder="e.g. Follow up with Priya about the Bali package quote we sent on Monday; offer a call on Thursday."
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onCloseAiDialog} color="inherit">
            Cancel
          </Button>
          <Button variant="contained" onClick={onGenerate} disabled={!aiContext.trim()}>
            Draft
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

ComposePane.propTypes = {
  title: PropTypes.string.isRequired,
  draft: PropTypes.shape({
    to: PropTypes.string,
    cc: PropTypes.string,
    bcc: PropTypes.string,
    subject: PropTypes.string,
    content: PropTypes.string,
  }).isRequired,
  onDraftChange: PropTypes.func.isRequired,
  showCc: PropTypes.bool,
  showBcc: PropTypes.bool,
  onToggleCc: PropTypes.func.isRequired,
  onToggleBcc: PropTypes.func.isRequired,
  recipientOptions: PropTypes.array,
  recipientsLoading: PropTypes.bool,
  onRecipientSearch: PropTypes.func.isRequired,
  attachments: PropTypes.array,
  uploadingAttachment: PropTypes.bool,
  onAttach: PropTypes.func.isRequired,
  onRemoveAttachment: PropTypes.func.isRequired,
  draftId: PropTypes.string,
  savingDraft: PropTypes.bool,
  sending: PropTypes.bool,
  onSaveDraft: PropTypes.func.isRequired,
  onDiscard: PropTypes.func.isRequired,
  onSend: PropTypes.func.isRequired,
  aiEnabled: PropTypes.bool,
  licenseLoading: PropTypes.bool,
  generating: PropTypes.bool,
  aiError: PropTypes.string,
  aiDialogOpen: PropTypes.bool,
  aiContext: PropTypes.string,
  onAiContextChange: PropTypes.func.isRequired,
  onOpenAiDialog: PropTypes.func.isRequired,
  onCloseAiDialog: PropTypes.func.isRequired,
  onGenerate: PropTypes.func.isRequired,
};

ComposePane.defaultProps = {
  showCc: false,
  showBcc: false,
  recipientOptions: [],
  recipientsLoading: false,
  attachments: [],
  uploadingAttachment: false,
  draftId: null,
  savingDraft: false,
  sending: false,
  aiEnabled: false,
  licenseLoading: false,
  generating: false,
  aiError: '',
  aiDialogOpen: false,
  aiContext: '',
};

export default ComposePane;
